import { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import dklogo from "../../img/PCLC.svg";
import { requestPasswordReset, submitPasswordReset } from "../../services/auth";

const STEP_REQUEST = 'request';
const STEP_CONFIRM = 'confirm';
const STEP_DONE = 'done';

const RESEND_COOLDOWN_MS = 60_000;

const PasswordRecovery = () => {
    const [step, setStep] = useState(STEP_REQUEST);
    const [username, setUsername] = useState("");
    const [destination, setDestination] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const [resendAvailableAt, setResendAvailableAt] = useState(0);
    const [now, setNow] = useState(() => Date.now());

    // Tick the clock once per second while a cooldown is active. Self-cancels
    // when the timer expires (effect re-runs and the early return fires).
    useEffect(() => {
        const remaining = resendAvailableAt - now;
        if (remaining <= 0) return;
        const id = setTimeout(() => setNow(Date.now()), 1000);
        return () => clearTimeout(id);
    }, [resendAvailableAt, now]);

    const resendSecondsLeft = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
    const canResend = !busy && resendSecondsLeft === 0;

    async function handleRequestCode() {
        if (!username.trim()) {
            setErrMsg("Please enter your username.");
            return;
        }
        setErrMsg("");
        setBusy(true);
        const result = await requestPasswordReset(username.trim());
        setBusy(false);

        if (result.success) {
            setDestination(result.destination || "");
            setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
            setNow(Date.now());
            setStep(STEP_CONFIRM);
        } else {
            setErrMsg(result.error);
        }
    }

    async function handleConfirmReset() {
        if (!code.trim()) {
            setErrMsg("Please enter the code from your email.");
            return;
        }
        if (newPassword.length < 6) {
            setErrMsg("Password must be at least 6 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrMsg("Passwords do not match.");
            return;
        }
        setErrMsg("");
        setBusy(true);
        const result = await submitPasswordReset(username.trim(), code.trim(), newPassword);
        setBusy(false);

        if (result.success) {
            setStep(STEP_DONE);
        } else {
            setErrMsg(result.error);
        }
    }

    async function handleResendCode() {
        if (!canResend) return;
        setErrMsg("");
        setBusy(true);
        const result = await requestPasswordReset(username.trim());
        setBusy(false);
        if (result.success) {
            setDestination(result.destination || "");
            setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
            setNow(Date.now());
            setErrMsg("");
        } else {
            setErrMsg(result.error);
        }
    }

    return (
        <div className="auth-card">
            <img src={dklogo} alt="PalCare Logo" className="auth-card__logo" />

            {step === STEP_REQUEST && (
                <>
                    <div className="auth-card__icon">
                        <i className="fe fe-lock" />
                    </div>
                    <h2 className="auth-card__title">Reset Password</h2>
                    <p className="auth-card__message">
                        Enter your username and we'll email you a verification code to reset your password.
                    </p>
                    <form className="auth-form" onSubmit={(e) => { e.preventDefault(); handleRequestCode(); }}>
                        <div className="auth-field">
                            <label className="auth-field__label" htmlFor="recoveryUsername">Username</label>
                            <input
                                id="recoveryUsername"
                                type="text"
                                className="auth-field__input"
                                placeholder="Enter your username"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>

                        {errMsg && <div className="auth-error">{errMsg}</div>}

                        <button
                            type="submit"
                            className="auth-btn-primary"
                            disabled={busy}
                        >
                            {busy ? <><span className="auth-spinner" /> Sending...</> : "Send Code"}
                        </button>

                        <div style={{ textAlign: "center" }}>
                            <Link to="/" className="auth-link">Back to Sign In</Link>
                        </div>
                    </form>
                </>
            )}

            {step === STEP_CONFIRM && (
                <>
                    <div className="auth-card__icon">
                        <i className="fe fe-mail" />
                    </div>
                    <h2 className="auth-card__title">Enter Code</h2>
                    <p className="auth-card__message">
                        {destination
                            ? <>We sent a code to <strong>{destination}</strong>. Enter it below along with your new password.</>
                            : <>If an account exists, a code has been emailed. Enter it below along with your new password.</>}
                    </p>
                    <form className="auth-form" onSubmit={(e) => { e.preventDefault(); handleConfirmReset(); }}>
                        <div className="auth-field">
                            <label className="auth-field__label" htmlFor="recoveryCode">Verification Code</label>
                            <input
                                id="recoveryCode"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                className="auth-field__input"
                                placeholder="6-digit code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </div>

                        <div className="auth-field">
                            <label className="auth-field__label" htmlFor="recoveryNewPassword">New Password</label>
                            <input
                                id="recoveryNewPassword"
                                type="password"
                                autoComplete="new-password"
                                className="auth-field__input"
                                placeholder="At least 6 characters"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </div>

                        <div className="auth-field">
                            <label className="auth-field__label" htmlFor="recoveryConfirmPassword">Confirm Password</label>
                            <input
                                id="recoveryConfirmPassword"
                                type="password"
                                autoComplete="new-password"
                                className="auth-field__input"
                                placeholder="Re-enter new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </div>

                        {errMsg && <div className="auth-error">{errMsg}</div>}

                        <button
                            type="submit"
                            className="auth-btn-primary"
                            disabled={busy}
                        >
                            {busy ? <><span className="auth-spinner" /> Resetting...</> : "Reset Password"}
                        </button>

                        <p
                            style={{
                                textAlign: "center",
                                fontSize: "var(--font-size-xs)",
                                color: "var(--color-text-tertiary)",
                                margin: 0,
                            }}
                        >
                            Didn't receive an email within a few minutes? Double-check your username, or use Resend code below.
                        </p>

                        <div style={{ textAlign: "center" }}>
                            <button
                                type="button"
                                className="auth-link"
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: canResend ? "pointer" : "not-allowed",
                                    padding: 0,
                                    opacity: canResend ? 1 : 0.6,
                                }}
                                onClick={handleResendCode}
                                disabled={!canResend}
                            >
                                {resendSecondsLeft > 0
                                    ? `Resend code in ${resendSecondsLeft}s`
                                    : "Resend code"}
                            </button>
                            <span style={{ margin: "0 var(--space-2)" }}>·</span>
                            <Link to="/" className="auth-link">Back to Sign In</Link>
                        </div>
                    </form>
                </>
            )}

            {step === STEP_DONE && (
                <>
                    <div className="auth-card__icon">
                        <i className="fe fe-check-circle" />
                    </div>
                    <h2 className="auth-card__title">Password Reset</h2>
                    <p className="auth-card__message">
                        Your password has been updated. You can now sign in with your new password.
                    </p>
                    <Link to="/" className="auth-link">Back to Sign In</Link>
                </>
            )}
        </div>
    );
};

export default PasswordRecovery;
